import React, {useState} from "react";
import {useAuth} from "../../context/AuthContext";
import {useNavigate} from "react-router-dom";
import User from "../user/User";
import AppProps from "../../static/js/AppProps";
import { useLanguage } from '../../components/hooks/LanguageContext';

const Login = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const [usernameError, setUsernameError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [generalError, setGeneralError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const {login} = useAuth();
    const navigate = useNavigate();
    const { language } = useLanguage();

    const texts = {
        ru: {
            title: 'Вход',
            usernamePlaceholder: 'Имя пользователя',
            usernameRequired: 'Имя пользователя обязательно',
            passwordPlaceholder: 'Пароль',
            passwordRequired: 'Пароль обязателен',
            submitButton: 'Войти',
            submittingButton: 'Вход...',
            tokenError: 'Токен не получен. Проверьте, что сервер возвращает token в теле ответа.',
            invalidCredentials: 'Неверное имя пользователя или пароль.',
            validationError: 'Ошибка валидации.',
            authError: 'Ошибка при авторизации.',
            generalError: 'Произошла ошибка при попытке входа. Попробуйте позже.',
            showPassword: 'Показать пароль',
            hidePassword: 'Скрыть пароль'
        },
        en: {
            title: 'Sign In',
            usernamePlaceholder: 'Username',
            usernameRequired: 'Username is required',
            passwordPlaceholder: 'Password',
            passwordRequired: 'Password is required',
            submitButton: 'Sign In',
            submittingButton: 'Signing in...',
            tokenError: 'Token not received. Make sure the server returns a token in the response body.',
            invalidCredentials: 'Invalid username or password.',
            validationError: 'Validation error.',
            authError: 'Authentication error.',
            generalError: 'An error occurred while trying to log in. Please try again later.',
            showPassword: 'Show password',
            hidePassword: 'Hide password'
        }
    };

    const t = texts[language];

    const handleChange = (event) => {
        const {name, value} = event.target;
        if (name === 'username') {
            setUsername(value);
            setUsernameError('');
        } else if (name === 'password') {
            setPassword(value);
            setPasswordError('');
        }
        setGeneralError('');
    };

    const toggleShowPassword = () => {
        setShowPassword((prev) => !prev);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        setUsernameError('');
        setPasswordError('');
        setGeneralError('');
        setIsLoading(true);

        let hasError = false;
        if (!username.trim()) {
            setUsernameError(t.usernameRequired);
            hasError = true;
        }
        if (!password) {
            setPasswordError(t.passwordRequired);
            hasError = true;
        }
        if (hasError) {
            setIsLoading(false);
            return;
        }

        try {
            const body = { username, password };

            const response = await fetch(
                `${AppProps.spring_server_url}/auth/sign-in`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                }
            );

            const responseText = await response.text();
            // #region agent log
            fetch('http://127.0.0.1:7591/ingest/d2880673-f438-4573-9075-68f79f8bcef7',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'da59d4'},body:JSON.stringify({sessionId:'da59d4',runId:'login-fix',hypothesisId:'H1',location:'Login.jsx:sign-in',message:'sign-in response',data:{apiBase:AppProps.spring_server_url,status:response.status,ok:response.ok,hasToken:responseText.includes('"token"'),errSnippet:response.ok?null:responseText.slice(0,180)},timestamp:Date.now()})}).catch(()=>{});
            // #endregion
            let data;

            try {
                data = JSON.parse(responseText);
            } catch {
                console.warn('Ответ сервера не JSON:', responseText);
                data = { message: responseText };
            }

            if (response.ok) {
                const token = data.token || data.accessToken || null;
                if (token) {
                    localStorage.setItem('token', token);
                    login({ username }, token);
                    navigate(User.link);
                } else {
                    setGeneralError(t.tokenError);
                }
            } else {
                if (data.errors) {
                    if (data.errors.username) setUsernameError(data.errors.username);
                    if (data.errors.password) setPasswordError(data.errors.password);
                }

                if (response.status === 401) {
                    setGeneralError(data.message || t.invalidCredentials);
                } else if (response.status === 422) {
                    setGeneralError(data.message || t.validationError);
                } else {
                    setGeneralError(data.message || t.authError);
                }
            }
        } catch (error) {
            console.error(error);
            setGeneralError(t.generalError);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <style>{`
                input[type="password"]::-ms-reveal {
                    display: none;
                }
            `}</style>

            <section data-bs-version="5.1" className="form5 cid-uEnGCplImi" id="form02-t">
                <div className="container-fluid">
                    <div className="row justify-content-center">
                        <div className="col-12 content-head">
                            <div className="mbr-section-head mb-4">
                                <h3 className="mbr-section-title mbr-fonts-style text-primary display-1 align-center">
                                    <strong>{t.title}</strong>
                                </h3>
                            </div>
                        </div>
                    </div>
                    <div className="row justify-content-center">
                        <div className="col-12 col-md-10 col-lg-8 col-xl-6 mx-auto mbr-form" data-form-type="formoid">
                            <form onSubmit={handleSubmit} className="mbr-form form-with-styler"
                                  data-form-title="Form Name">
                                <div className="dragArea row">
                                    {generalError && (
                                        <div className="col-12 mb-4">
                                            <div className="alert alert-danger">{generalError}</div>
                                        </div>
                                    )}

                                    <div className="col-md col-sm-12 form-group mb-4" data-for="username">
                                        <input
                                            type="text"
                                            name="username"
                                            placeholder={t.usernamePlaceholder}
                                            className={`form-control form-control-lg ${usernameError ? 'is-invalid' : ''}`}
                                            value={username}
                                            onChange={handleChange}
                                            disabled={isLoading}
                                            required
                                        />
                                        {usernameError &&
                                            <div className="invalid-feedback d-block">
                                                {usernameError}
                                            </div>
                                        }
                                    </div>

                                    <div
                                        className="col-12 form-group mb-4 position-relative"
                                        data-for="password"
                                        style={{position: 'relative'}}>
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            name="password"
                                            placeholder={t.passwordPlaceholder}
                                            className={
                                                `form-control form-control-lg 
                                                ${passwordError ? 'is-invalid' : ''}`
                                            }
                                            value={password}
                                            onChange={handleChange}
                                            disabled={isLoading}
                                            required
                                        />
                                        {passwordError &&
                                            <div className="invalid-feedback d-block">{passwordError}</div>}

                                        <button
                                            type="button"
                                            onClick={toggleShowPassword}
                                            style={{
                                                position: 'absolute',
                                                top: '50%',
                                                right: '15px',
                                                transform: 'translateY(-50%)',
                                                background: 'transparent',
                                                border: 'none',
                                                cursor: 'pointer',
                                                padding: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '1.8rem',
                                                color: '#6c757d',
                                                userSelect: 'none',
                                            }}
                                            aria-label={showPassword ? t.hidePassword : t.showPassword}
                                            tabIndex={-1}
                                        >
                                            <span className={`fi ${showPassword ? 'fi-rr-eye-crossed' : 'fi-rr-eye'}`}/>
                                        </button>
                                    </div>

                                    <div className="col-12 align-center mbr-section-btn">
                                        <button type="submit" className="btn btn-danger btn-lg display-4"
                                                disabled={isLoading}>
                                            {isLoading ? t.submittingButton : t.submitButton}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

Login.link = "/login";

export default Login;